/* ─────────────────────────────────────────────────────────────
   The previous Scheduled Reports list — antd Row/Col scaffolding around the
   antd-backed CustomTable, with the rows hardcoded in the component and the
   page fetching resources (connections) it never used. Replaced by the version
   below, built on the shared table at components/common/DataTable.
   Kept commented rather than deleted, per house convention.
   ─────────────────────────────────────────────────────────────

import React,{useState} from "react";
import { useHistory } from "react-router-dom";
import DashboardView from "../DashboardView";
import { Row, Col, Button, Input, Typography, Popover, Space, Drawer } from "antd";
import { CalendarOutlined, CloseCircleFilled, DeleteOutlined, EditOutlined, EyeOutlined, FileDoneOutlined, MailOutlined, MessageOutlined, MoreOutlined, PlusOutlined, WhatsAppOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getResourcesAsync,
  getResourceTypesAsync,
} from "../../redux/resourceSlice";
import CustomTable from "../Common/CustomTable";
import "./reports-logs.css"
import PopoverMenu from "../Common/PopoverMenu";
import Timeline from "../Common/Timeline";

const { Search } = Input;

export default function ScheduleReports() {
  const history = useHistory();
  const { Search } = Input;
  const [data, setData] = React.useState([]);

  const resources = useSelector((state) => state.resources);
  const groups = useSelector((state) => state.boards.groups);
  const dispatch = useDispatch();

  ... see git history for the full previous implementation ...
}
   ───────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import SchedulePageLayout from "./SchedulePageLayout";
import CustomTable from "../../components/common/DataTable";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import ScheduleSummaryPanel from "./ScheduleSummaryPanel";
import { fetchSchedules, softDeleteSchedule, actionError } from "./scheduleApi";
import { FILE_TYPE_VALUES, rowFromSchedule } from "./scheduleMappers";
/* Imported as a component rather than a URL: the file carries a hardcoded
   fill="#94A3B8", and an <img> cannot be recoloured from CSS — so the icon would stay
   grey while the button darkens on hover. Inlined, a CSS `fill` overrides that
   presentation attribute. Same reason DashboardList inlines reports.svg. */
import { ReactComponent as ScheduleLogIcon } from "../../assets/icons/schedule-log.svg";
import "./ScheduleReports.css";

/* ── Inline icons ────────────────────────────────────────────
   Drawn here rather than pulled from an icon package: the page is meant to be
   plain React + CSS, and each of these needs to take its colour from the pill
   or button it sits in. */
const Icon = {
  search: (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
      <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M13 13l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
      <rect x="2.5" y="4" width="15" height="13.5" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2.5 8h15M6.5 2.5v3M13.5 2.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M6.8 10.2l2.2 2.2 4.2-4.4"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  draft: (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M11.5 2.5H6a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V7l-4.5-4.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M11.5 2.5V7H16" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  file: (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
      <path
        d="M11.5 2.5H6a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V7l-4.5-4.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M11.5 2.5V7H16M7 11h6M7 14h4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  mail: (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true">
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.8" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 4.6l6 4 6-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor" aria-hidden="true">
      <path d="M8 1.6a6.3 6.3 0 00-5.4 9.5l-.9 3.3 3.4-.9A6.3 6.3 0 108 1.6zm0 1.4a4.9 4.9 0 014.9 4.9A4.9 4.9 0 015.6 12l-.3-.2-2 .5.5-2-.2-.3A4.9 4.9 0 018 3zm-2.2 2.4c-.1 0-.3 0-.4.2-.2.2-.6.5-.6 1.3 0 .8.6 1.5.7 1.6.1.1 1.1 1.8 2.8 2.4 1.4.5 1.7.4 2 .4.4 0 1-.4 1.2-.9.1-.4.1-.8.1-.9 0-.1-.2-.1-.3-.2l-1.1-.5c-.2 0-.3-.1-.4.1l-.5.7c-.1.1-.2.1-.4 0-.2-.1-.8-.3-1.5-1-.6-.5-1-1.1-1.1-1.3-.1-.2 0-.3.1-.4l.3-.4c.1-.1.1-.2.2-.3v-.3l-.5-1.2c-.1-.3-.3-.3-.4-.3h-.2z" />
    </svg>
  ),
  sms: (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true">
      <path
        d="M14 9.5a2 2 0 01-2 2H6l-3.5 2.5v-2.5a2 2 0 01-.5-1.3V4.5a2 2 0 012-2h8a2 2 0 012 2v5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.6V8l2.3 1.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  eye: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="2.6" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M1 8s2.6-4.6 7-4.6S15 8 15 8s-2.6 4.6-7 4.6S1 8 1 8z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  ),
  edit: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path d="M11.2 2.3l2.5 2.5-8 8-3.2.7.7-3.2 8-8z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  ),
  trash: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path
        d="M2.5 4h11M6 4V2.6h4V4M6.4 7v4.6M9.6 7v4.6M3.6 4l.7 8.6a1.2 1.2 0 001.2 1.1h5a1.2 1.2 0 001.2-1.1L12.4 4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  /* The logs icon is no longer here — it comes from assets/icons/schedule-log.svg,
     imported as a component at the top of this file. */
};

/* ── Filter options ────────────────────────────────────────── */
const STATUS_OPTIONS = ["Draft", "Published"];
const SCHEDULE_TYPE_OPTIONS = ["Email", "WhatsApp", "SMS"];
// const FILE_TYPE_OPTIONS = ["PDF", "HTML", "Xls", "JPEG", "Link", "CSV"];
/* The types a schedule can actually be saved with — the create form's list.
   The old one offered JPEG and Link (dropped from the form) and missed PNG, so
   two options could never match anything and PNG schedules could not be found. */
const FILE_TYPE_OPTIONS = FILE_TYPE_VALUES;

/* ── Cell renderers ────────────────────────────────────────── */
const ScheduleTypePill = ({ type }) => {
  const key = String(type || "").toLowerCase();
  const glyph = key === "whatsapp" ? Icon.whatsapp : key === "sms" ? Icon.sms : Icon.mail;
  return (
    <span className={`sch-type-pill sch-type-${key}`}>
      {glyph}
      {type}
    </span>
  );
};

const StatusPill = ({ status }) => (
  <span className={`sch-status-pill sch-status-${String(status || "").toLowerCase()}`}>
    <span className="sch-status-dot" />
    {status}
  </span>
);

export default function ScheduleReports() {
  const history = useHistory();

  const [schedules, setSchedules] = useState([]);
  const [listState, setListState] = useState("loading"); // loading | ready | error

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterScheduleType, setFilterScheduleType] = useState("all");
  const [filterFileType, setFilterFileType] = useState("all");

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // The row behind the summary panel; null when the panel is closed.
  const [summaryRow, setSummaryRow] = useState(null);

  /* POST /v1/scheduler/all takes the organization, which login writes to
     localStorage — it is not on the JWT this app decodes. */
  const load = useCallback(async () => {
    setListState("loading");
    try {
      const records = await fetchSchedules(localStorage.getItem("orgID") || "");
      /* Newest first. The scheduler returns them in storage order, so a schedule created
         a moment ago landed at the bottom of the last page. Sorted here rather than in
         the table so the order survives filtering and paging.

         createdTs falls back to the id's embedded timestamp for records whose
         CreatedDate is the zero value - see createdTimestamp. */
      setSchedules(records.map(rowFromSchedule).sort((a, b) => b.createdTs - a.createdTs));
      setListState("ready");
    } catch (error) {
      setSchedules([]);
      setListState("error");
      toast.error(actionError(error, "Loading schedules failed."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const liveSchedules = schedules;

  /* Counts are of the whole list, not the filtered view — they summarise the
     account, so searching must not move them. */
  const stats = useMemo(
    () => ({
      total: liveSchedules.length,
      published: liveSchedules.filter((s) => s.status === "Published").length,
      draft: liveSchedules.filter((s) => s.status === "Draft").length,
    }),
    [liveSchedules],
  );

  const filteredRows = useMemo(
    () =>
      liveSchedules.filter((row) => {
        const matchSearch = row.reportName.toLowerCase().includes(searchQuery.toLowerCase());
        const matchStatus = filterStatus === "all" || row.status === filterStatus;
        // const matchType = filterScheduleType === "all" || row.scheduleType === filterScheduleType;
        // const matchFile = filterFileType === "all" || row.fileType === filterFileType;
        /* Case-insensitive: the payload stores FileType lowercased ("pdf") while
           the options read "PDF", so an exact match found nothing for any file
           type. Schedule Type is compared the same way so a casing change on
           the API cannot break that filter too. */
        const same = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();
        const matchType = filterScheduleType === "all" || same(row.scheduleType, filterScheduleType);
        const matchFile = filterFileType === "all" || same(row.fileType, filterFileType);
        return matchSearch && matchStatus && matchType && matchFile;
      }),
    [liveSchedules, searchQuery, filterStatus, filterScheduleType, filterFileType],
  );

  /* ── Navigation ─────────────────────────────────────────
     Create and Edit are the same page — the route decides which. */
  const goToCreate = () => history.push("/scheduled-reports/create");
  const goToEdit = (row) => history.push(`/scheduled-reports/edit/${row.id}`);
  const goToLogs = (row) => history.push(`/scheduled-reports/logs/${row.id}`);

  /* There is no delete endpoint, so removal is an edit that sets IsDeleted —
     see softDeleteSchedule. The row is only dropped from the list once the
     scheduler confirms: deleting locally on an error would leave a schedule
     still delivering with nothing on screen to stop it. */
  const confirmDelete = async () => {
    const row = pendingDelete;
    if (!row) return;

    setDeleting(true);
    try {
      // Takes the id, not the row: the full record has to be read back before
      // edit will accept it — see softDeleteSchedule.
      await softDeleteSchedule(row.id);
      setPendingDelete(null);
      if (summaryRow?.id === row.id) setSummaryRow(null);
      toast.success(`"${row.reportName}" was deleted.`);
      await load();
    } catch (error) {
      setPendingDelete(null);
      /* The scheduler's own Result text when it gave one, otherwise say
         which action failed rather than a bare "something went wrong". */
      toast.error(actionError(error, "Delete schedule failed."));
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      title: "Report Name",
      dataIndex: "reportName",
      key: "reportName",
      render: (value, row) => (
        <div className="sch-name-cell">
          <span className="sch-name-icon">{Icon.file}</span>
          <div>
            <div className="sch-name-text" onClick={() => goToEdit(row)}>
              {value}
            </div>
            {/* The file type badge alone now. A second label sat beside it showing the
                vertical, which no longer exists - and there is nothing on a schedule
                record to put in its place: no ApplicationID, and ServiceName is
                "WitmegReport" on every one. See rowFromSchedule. */}
            <div className="sch-name-meta">
              <span className={`sch-file-badge sch-file-${String(row.fileType || "").toLowerCase()}`}>
                {row.fileType}
              </span>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Schedule Type",
      dataIndex: "scheduleType",
      key: "scheduleType",
      nowrap: true,
      render: (value) => <ScheduleTypePill type={value} />,
    },
    {
      title: "Frequency & Time",
      dataIndex: "frequency",
      key: "frequency",
      /* Narrowed from 30%. The longest thing it holds is "Every weekday (Monday to
         Friday)"-style text, which still fits on one line here, and the 30% was pushing
         Status and Actions into the right-hand edge. */
      width: "20%",
      render: (value) => (
        <span className="sch-frequency">
          {Icon.clock}
          {value}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      nowrap: true,
      render: (value) => <StatusPill status={value} />,
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      nowrap: true,
      render: (_value, row) => (
        <div className="sch-actions">
          <button type="button" className="sch-action-btn" title="View summary" onClick={() => setSummaryRow(row)}>
            {Icon.eye}
          </button>
          <button type="button" className="sch-action-btn" title="Edit schedule" onClick={() => goToEdit(row)}>
            {Icon.edit}
          </button>
          {/* Schedule logs — the destination page is still the old one; its UI
              is updated in a later pass. */}
          <button
            type="button"
            className="sch-action-btn sch-action-log"
            title="Schedule logs"
            onClick={() => goToLogs(row)}
          >
            <ScheduleLogIcon />
          </button>
          <button
            type="button"
            className="sch-action-btn sch-action-danger"
            title="Delete schedule"
            onClick={() => setPendingDelete(row)}
          >
            {Icon.trash}
          </button>
        </div>
      ),
    },
  ];

  return (
    /* App chrome, page frame and the title block all come from the section
       layout — see SchedulePageLayout. */
    <SchedulePageLayout title="Scheduled Reports" actionLabel="New Schedule" onAction={goToCreate}>
      {/* Stat cards */}
      <div className="sch-stats">
        <div className="sch-stat-card">
          <span className="sch-stat-icon sch-stat-icon--total">{Icon.calendar}</span>
          <div>
            <div className="sch-stat-value">{stats.total}</div>
            <div className="sch-stat-label">Total Schedules</div>
          </div>
        </div>
        <div className="sch-stat-card">
          <span className="sch-stat-icon sch-stat-icon--published">{Icon.check}</span>
          <div>
            <div className="sch-stat-value">{stats.published}</div>
            <div className="sch-stat-label">Published</div>
          </div>
        </div>
        <div className="sch-stat-card">
          <span className="sch-stat-icon sch-stat-icon--draft">{Icon.draft}</span>
          <div>
            <div className="sch-stat-value">{stats.draft}</div>
            <div className="sch-stat-label">Drafts</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="sch-controls">
        <div className="sch-search-wrap">
          <span className="sch-search-icon">{Icon.search}</span>
          <input
            className="sch-search-input"
            type="text"
            placeholder="Search reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="sch-filters">
          <select className="sch-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <select
            className="sch-select"
            value={filterScheduleType}
            onChange={(e) => setFilterScheduleType(e.target.value)}
          >
            <option value="all">All Schedule Types</option>
            {SCHEDULE_TYPE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <select className="sch-select" value={filterFileType} onChange={(e) => setFilterFileType(e.target.value)}>
            <option value="all">All File Types</option>
            {FILE_TYPE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* The list. resetPageOn: changing a filter or the search term drops the
          table back to page one, so filtering from page 3 never lands on an
          empty page. */}
      {/* Failures are toasted rather than banner-ed, but a toast disappears —
          so the empty state carries the retry, which is where someone looks
          when the table has nothing in it. */}
      <CustomTable
        tableHeaders={columns}
        tableData={filteredRows}
        rowKey="id"
        loading={listState === "loading"}
        emptyText={
          listState === "error" ? (
            <div className="sch-empty-error">
              <span>Could not load schedules.</span>
              <button type="button" className="sch-retry-btn" onClick={load}>
                Retry
              </button>
            </div>
          ) : (
            "No scheduled reports found."
          )
        }
        minWidth={860}
        resetPageOn={`${searchQuery}|${filterStatus}|${filterScheduleType}|${filterFileType}`}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        danger
        title="Delete this schedule?"
        message={`"${pendingDelete?.reportName}" will no longer be listed or delivered.`}
        confirmText={deleting ? "Deleting..." : "Delete"}
        // Both buttons disabled while the delete runs — a second click fired it again.
        busy={deleting}
        onCancel={() => (deleting ? null : setPendingDelete(null))}
        onConfirm={confirmDelete}
      />

      {/* Summary side panel — the eye icon. */}
      <ScheduleSummaryPanel
        open={Boolean(summaryRow)}
        schedule={summaryRow}
        onClose={() => setSummaryRow(null)}
        onEdit={(row) => {
          setSummaryRow(null);
          goToEdit(row);
        }}
      />
    </SchedulePageLayout>
  );
}
